import { useCallback, useState } from "react";
import type { AlertVariant } from "../../ds";
import { ApiError, api, errorText } from "../api";

export interface ActionNote {
  variant: AlertVariant;
  text: string;
}

/**
 * Aktionen einer Sektion auslösen — mit Busy-Zustand und einer Rückmeldung,
 * die Offline (mild) von echten Fehlern (rot) trennt. Lädt bewusst **nichts**
 * automatisch nach: nach einer Aktion klickt der Bediener „Vom Router laden“.
 */
export function useDeviceAction(deviceId: string) {
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<ActionNote | null>(null);

  const run = useCallback(
    async <T>(
      action: string,
      params?: Record<string, unknown>,
      okText?: string,
    ): Promise<T | null> => {
      setBusy(action);
      setNote(null);
      try {
        const r = await api.runAction<T>(deviceId, action, params);
        if (okText) setNote({ variant: "success", text: okText });
        return r.data;
      } catch (e) {
        setNote(
          e instanceof ApiError && e.isOffline
            ? {
                variant: "warning",
                text: "Router nicht erreichbar — die Aktion wurde nicht ausgeführt.",
              }
            : { variant: "danger", text: errorText(e) },
        );
        return null;
      } finally {
        setBusy(null);
      }
    },
    [deviceId],
  );

  return { busy, note, setNote, run };
}
